import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from '@mui/material';
import { analyzePresentationImport, cancelPresentationImport, commitPresentationImport } from '../api';
import { COPY } from '../presentationCopy';
import { ImportIssue, ImportOffPaletteColor, ImportPaletteColor, ImportSummary } from '../types';
import { PRES } from '../chrome';
import ImportColorMatch, { isImportColorMapComplete } from './ImportColorMatch';

interface ImportDialogProps {
  open: boolean;
  presentationId: string;
  kind?: 'pptx' | 'pdf';
  onClose: () => void;
  onImported: (summary: ImportSummary) => void;
}

const ISSUE_LABEL: Record<ImportIssue['kind'], string> = {
  unlinked_image: 'Olänkad bild',
  missing_media: 'Saknad bildfil',
  unsupported: 'Stöds inte',
  unplaced_image: 'Bild utan position',
};

function asSummary(result: {
  slideCount?: number;
  averageConversion?: number;
  convertedBlocks?: number;
  rasterBlocks?: number;
  skippedAnimations?: number;
  issues?: ImportIssue[];
  slides?: ImportSummary['slides'];
}): ImportSummary | null {
  if (
    result.slideCount == null
    || result.averageConversion == null
    || result.convertedBlocks == null
    || result.rasterBlocks == null
    || result.skippedAnimations == null
    || !result.slides
  ) {
    return null;
  }
  return {
    slideCount: result.slideCount,
    averageConversion: result.averageConversion,
    convertedBlocks: result.convertedBlocks,
    rasterBlocks: result.rasterBlocks,
    skippedAnimations: result.skippedAnimations,
    issues: result.issues || [],
    slides: result.slides,
  };
}

const ImportDialog: React.FC<ImportDialogProps> = ({ open, presentationId, kind = 'pptx', onClose, onImported }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [offPaletteColors, setOffPaletteColors] = useState<ImportOffPaletteColor[]>([]);
  const [palette, setPalette] = useState<ImportPaletteColor[]>([]);
  const [colorMap, setColorMap] = useState<Record<string, string>>({});

  const reset = () => {
    setBusy(false);
    setError(null);
    setSummary(null);
    setConfirmed(false);
    setSessionId(null);
    setOffPaletteColors([]);
    setPalette([]);
    setColorMap({});
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const discardSession = async (id?: string | null) => {
    if (!id) return;
    try {
      await cancelPresentationImport(presentationId, id);
    } catch {
      /* already gone or network — deck is unchanged */
    }
  };

  const close = async () => {
    if (busy) return;
    const pending = summary ? null : sessionId;
    await discardSession(pending);
    onClose();
  };

  const fail = (err: any) => {
    const status = err?.response?.status;
    if (status === 413) {
      setError(err?.response?.data?.message || 'Filen är för stor. Maxstorlek är 200 MB.');
    } else {
      setError(err?.response?.data?.message || 'Importen misslyckades.');
    }
  };

  const onFile = async (file?: File) => {
    if (!file) return;
    const previous = sessionId;
    setBusy(true);
    setError(null);
    setSummary(null);
    setSessionId(null);
    setOffPaletteColors([]);
    setColorMap({});
    try {
      await discardSession(previous);
      const result = await analyzePresentationImport(presentationId, kind, file);
      if (result.committed) {
        const imported = asSummary(result);
        if (!imported) {
          setError('Importen misslyckades.');
          return;
        }
        setSummary(imported);
        onImported(imported);
        return;
      }
      setSessionId(result.sessionId);
      setOffPaletteColors(result.offPaletteColors || []);
      setPalette(result.palette || []);
    } catch (err: any) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const onCommit = async () => {
    if (!sessionId || !isImportColorMapComplete(offPaletteColors, colorMap)) return;
    setBusy(true);
    setError(null);
    try {
      const result = await commitPresentationImport(presentationId, kind, sessionId, colorMap);
      setSessionId(null);
      setOffPaletteColors([]);
      setSummary(result);
      onImported(result);
    } catch (err: any) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const matching = Boolean(sessionId && offPaletteColors.length && !summary);
  const canCommit = matching && isImportColorMapComplete(offPaletteColors, colorMap);
  const issues = summary?.issues || [];

  return (
    <Dialog open={open} onClose={busy ? undefined : () => void close()} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: PRES.textPrimary }}>{kind === 'pdf' ? COPY.importPdf : COPY.importPptx}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: PRES.muted }}>
          {kind === 'pdf'
            ? COPY.importPdfIntro
            : 'Importen ersätter alla nuvarande sidor. En ögonblicksbild sparas först så att du kan återställa. Text, bilder och enkla former blir Athena-block på rätt plats. Maxstorlek 200 MB.'}
        </Typography>
        {!matching && !summary && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
              Jag förstår att alla sidor ersätts.
            </label>
            <Button variant="contained" component="label" disabled={busy || !confirmed} sx={{ bgcolor: PRES.interactive }}>
              {kind === 'pdf' ? COPY.importPdfChoose : 'Välj .pptx'}
              <input hidden type="file" accept={kind === 'pdf' ? '.pdf,application/pdf' : '.pptx'} onChange={(event) => void onFile(event.target.files?.[0])} />
            </Button>
          </>
        )}
        {busy && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" sx={{ color: PRES.muted }}>
              {matching || sessionId
                ? COPY.importCommitBusy
                : kind === 'pdf' ? COPY.importPdfBusy : 'Konverterar till Athena-format…'}
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {matching && (
          <ImportColorMatch
            colors={offPaletteColors}
            palette={palette}
            value={colorMap}
            disabled={busy}
            onChange={setColorMap}
          />
        )}
        {summary && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="subtitle1" sx={{ color: PRES.textPrimary, fontWeight: 700 }}>
              {summary.averageConversion}% konverterade till Athena-format
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {summary.slideCount} sidor. {summary.convertedBlocks} block konverterade, {summary.rasterBlocks} som statisk bild.
              {summary.skippedAnimations > 0 ? ` ${summary.skippedAnimations} animationer hoppades över.` : ''}
            </Typography>
            <Box sx={{ mt: 2, maxHeight: 280, overflow: 'auto', border: `1px solid ${PRES.line}`, borderRadius: 1 }}>
              {summary.slides.map((slide, index) => (
                <Box key={slide.slideId} sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 1, borderBottom: `1px solid ${PRES.line}` }}>
                  <Typography variant="body2" sx={{ width: 72, color: PRES.textPrimary, fontWeight: 600 }}>Sida {index + 1}</Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {slide.detected.text} text, {slide.detected.image} bild, {slide.detected.shape} form
                    {slide.detected.raster ? `, ${slide.detected.raster} raster` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ color: slide.conversionProgress >= 70 ? PRES.textPrimary : PRES.warn }}>
                    {slide.conversionProgress}%
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 2, color: PRES.textPrimary }}>Checklista</Typography>
            {issues.length === 0 ? (
              <Typography variant="body2" sx={{ color: PRES.muted }}>Inga olänkade bilder eller objekt som hoppats över.</Typography>
            ) : (
              <Box sx={{ mt: 1, maxHeight: 180, overflow: 'auto' }}>
                {issues.map((issue, index) => (
                  <Typography key={`${issue.kind}-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                    Slide {issue.slideIndex + 1}: {ISSUE_LABEL[issue.kind]} — {issue.detail}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {matching ? (
          <>
            <Button onClick={() => void close()} disabled={busy} sx={{ color: PRES.textPrimary }}>{COPY.cancel}</Button>
            <Button
              onClick={() => void onCommit()}
              disabled={busy || !canCommit}
              variant="contained"
              sx={{ bgcolor: PRES.interactive }}
            >
              {COPY.importCommit}
            </Button>
          </>
        ) : (
          <Button onClick={() => void close()} disabled={busy} sx={{ color: PRES.textPrimary }}>
            {summary ? 'Öppna i editorn' : 'Stäng'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
